import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/ui/Button";
import { Tag } from "primereact/tag";

const buildPhaseBadge = (t) => ({
    pre_match:   { label: t("smart_ads.phase_pre_match"),   cls: "bg-blue-100 text-blue-700"     },
    first_half:  { label: t("smart_ads.phase_first_half"),  cls: "bg-green-100 text-green-700"   },
    halftime:    { label: t("smart_ads.phase_halftime"),    cls: "bg-yellow-100 text-yellow-800" },
    second_half: { label: t("smart_ads.phase_second_half"), cls: "bg-green-100 text-green-700"   },
    extra_time:  { label: t("smart_ads.phase_extra_time"),  cls: "bg-orange-100 text-orange-700" },
    fulltime:    { label: t("smart_ads.phase_fulltime"),    cls: "bg-gray-100 text-gray-600"     },
    cancelled:   { label: t("status.cancelled"),            cls: "bg-red-100 text-red-600"       },
});

const STATUS_SEVERITY = { active: "success", completed: "info", cancelled: "danger" };

export default function SmartAdScheduleCard({ schedule, onDelete, onViewLogs }) {
    const { t }     = useTranslation();
    const PHASE_BADGE = buildPhaseBadge(t);
    const ms        = schedule.match_schedule ?? {};
    const fd        = ms.fixture_data ?? {};
    const badge     = schedule.current_phase ? PHASE_BADGE[schedule.current_phase] : null;
    const ruleCount = schedule.phase_rules?.length ?? 0;

    return (
        <div className="border rounded-xl p-4 bg-white shadow-sm">
            {/* Match header */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                    {fd.league_logo && <img src={fd.league_logo} alt="" className="w-4 h-4 object-contain" />}
                    <span>{fd.league_name}</span>
                </div>
                <Tag
                    value={schedule.status}
                    severity={STATUS_SEVERITY[schedule.status] ?? "info"}
                    className="text-xs capitalize"
                />
            </div>

            {/* Teams */}
            <div className="flex items-center justify-between my-2">
                <div className="flex items-center gap-2">
                    {fd.home_logo && <img src={fd.home_logo} alt="" className="w-7 h-7 object-contain" />}
                    <span className="font-semibold text-sm text-gray-800">{fd.home_team}</span>
                </div>
                <span className="text-xs text-gray-400 font-medium">vs</span>
                <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-gray-800">{fd.away_team}</span>
                    {fd.away_logo && <img src={fd.away_logo} alt="" className="w-7 h-7 object-contain" />}
                </div>
            </div>

            {/* Live phase badge */}
            {badge && (
                <div className="mb-2">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${badge.cls}`}>
                        {schedule.status === "active" && schedule.current_phase && !["fulltime","cancelled"].includes(schedule.current_phase) && (
                            <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse inline-block" />
                        )}
                        {badge.label}
                    </span>
                </div>
            )}

            {/* Meta */}
            <div className="text-xs text-gray-500 space-y-1 mt-1 mb-3">
                <div className="flex items-center gap-1">
                    <i className="pi pi-clock" />
                    <span>
                        {ms.kickoff_at
                            ? new Date(ms.kickoff_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })
                            : "—"}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    <i className="pi pi-list" />
                    <span>{t("smart_ads.n_phase_rules", { count: ruleCount })}</span>
                </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
                <Button
                    icon="pi pi-history"
                    label={t("smart_ads.logs_btn")}
                    className="p-button-info p-button-text p-button-sm"
                    onClick={() => onViewLogs(schedule.id)}
                    tooltip={t("smart_ads.view_logs_tooltip")}
                    tooltipOptions={{ position: "top" }}
                />
                <Button
                    icon="pi pi-trash"
                    className="p-button-danger p-button-text p-button-sm"
                    onClick={() => onDelete(schedule.id)}
                    tooltip={t("buttons.delete")}
                    tooltipOptions={{ position: "top" }}
                />
            </div>
        </div>
    );
}
